// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, ChangeDetectionStrategy, input, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HealthScoreVariant, HealthScoreRawData } from '@lfx/key-projects/key-projects.model';
import { HoverTriggerComponent } from '@lfx/shared/components/hover-trigger/hover-trigger.component';
import { KeyProjectsService } from '@lfx/features/key-projects/services/key-projects.service';
import { KeyProjectsHelper } from '@lfx/key-projects/key-projects.helper';
import { HealthScorePopoverContentComponent, HealthScoreData, HealthMetric } from './health-score-popover-content/health-score-popover-content.component';

@Component({
  selector: 'lfx-health-score-column',
  imports: [CommonModule, HoverTriggerComponent, HealthScorePopoverContentComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <lfx-hover-trigger
      [sticky]="true"
      placement="auto"
      popoverClass="health-score-popover">
      <div
        [class]="'health-badge lf-body-bold-sm ' + badgeClasses()"
        [attr.aria-label]="ariaLabel()">
        {{ displayText() }}
      </div>
      <div slot="popover-content" class="header-tooltip-content">
        <lfx-health-score-popover-content
          [variant]="variant()"
          [score]="score()"
          [customText]="customText()"
          [projectSlug]="projectSlug()"
          [data]="healthScoreData()">
        </lfx-health-score-popover-content>
      </div>
    </lfx-hover-trigger>
  `,
  styleUrls: ['./health-score-column.component.scss']
})
export class HealthScoreColumnComponent {
  // Input properties
  readonly variant = input.required<HealthScoreVariant>();
  readonly score = input<number | null>(null);
  readonly customText = input<string>('');
  readonly projectSlug = input<string>('');
  readonly description = input<string>('');
  readonly metrics = input<HealthMetric[]>([]);
  readonly healthScoreRawData = input<HealthScoreRawData | null>(null);

  // Service injection
  private readonly keyProjectsService = inject(KeyProjectsService);

  // Computed health score data for popover - no async loading needed!
  readonly healthScoreData = computed((): HealthScoreData => {
    const rawHealthScore = this.healthScoreRawData();
    const inputMetrics = this.metrics();
    const inputDescription = this.description();

        // If we have raw health score data from the backend, transform it
    if (rawHealthScore) {
      const detailedMetrics = this.keyProjectsService.transformToHealthMetrics(rawHealthScore);
      const finalMetrics = detailedMetrics.length > 0 ? detailedMetrics : inputMetrics.length > 0 ? inputMetrics : this.getDefaultMetrics();

      return {
        metrics: finalMetrics,
        description: inputDescription || '',
        isLoading: false,
        error: null
      };
    }

    // Fallback to input metrics or defaults
    const fallbackMetrics = inputMetrics.length > 0 ? inputMetrics : this.getDefaultMetrics();

    return {
      metrics: fallbackMetrics,
      description: inputDescription || '',
      isLoading: false,
      error: null
    };
  });

  protected readonly badgeClasses = computed(() => {
    return `health-badge--${this.variant()}`;
  });

  protected readonly displayText = computed(() => {
    if (this.customText()) {
      return this.customText();
    }

    if (this.score() !== null) {
      return this.score()?.toString();
    }

    // Use centralized labels
    return KeyProjectsHelper.getHealthScoreDisplayText(this.variant());
  });

  protected readonly ariaLabel = computed(() => {
    const text = this.displayText();
    return `Health score: ${text}`;
  });

  protected getDefaultMetrics(): HealthMetric[] {
    const placeholderColor = KeyProjectsHelper.getPlaceholderColor();
    return [
      {
        label: 'Contributors',
        percent: 0,
        value: 0,
        bgColor: placeholderColor
      },
      {
        label: 'Popularity',
        percent: 0,
        value: 0,
        bgColor: placeholderColor
      },
      {
        label: 'Development',
        percent: 0,
        value: 0,
        bgColor: placeholderColor
      },
      {
        label: 'Security',
        percent: 0,
        value: 0,
        bgColor: placeholderColor
      }
    ];
  }
}
